/**
 * Composition root. Wires sim + io + render.
 *
 * Exposes `window.__game` (dev-only handle) so E2E tests can read GameState
 * without scraping the canvas.
 */

import { createGame } from '../sim/init.ts';
import { step } from '../sim/step.ts';
import { attachKeyboard } from '../io/input.ts';
import { startLoop } from '../io/loop.ts';
import { render } from '../render/canvas.ts';
import { drawPauseBanner } from '../render/hud.ts';
import { attachPause } from '../io/pause.ts';
import { createAudioAdapter, createWebAudioSynth } from '../io/audio.ts';
import { createMusicPlayer } from '../io/music.ts';
import { loadSettings, saveSettings } from '../io/settings.ts';
import type { GameState } from '../sim/types.ts';
import { RALLYXY_SPRITE_URL, createRallySpriteSheet, type SpriteSheet } from '../render/sprites.ts';
import { computeCssScale } from '../render/scale.ts';

const RALLYXY_LOGO_URL = `${import.meta.env.BASE_URL}assets/rallyxy-logo.png`;

const canvasEl = document.getElementById('game');
if (!(canvasEl instanceof HTMLCanvasElement)) {
  throw new Error('canvas#game not found in index.html');
}
const canvas = canvasEl;

const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('2D canvas context unavailable');

// Scale the canvas via CSS to fill the browser window while keeping its
// internal resolution fixed. Re-fires on every window/fullscreen change so
// pixel-art crispness is preserved at integer multiples whenever possible.
function applyCanvasScale(): void {
  const s = computeCssScale(canvas.width, canvas.height, window.innerWidth, window.innerHeight);
  canvas.style.width = `${Math.round(canvas.width * s)}px`;
  canvas.style.height = `${Math.round(canvas.height * s)}px`;
}
applyCanvasScale();
window.addEventListener('resize', applyCanvasScale);
document.addEventListener('fullscreenchange', applyCanvasScale);

// Choose a seed that is fixed by default for reproducible dev sessions.
// A `?seed=...` query string overrides it for quick experiments.
const params = new URLSearchParams(window.location.search);
const seed = Number(params.get('seed') ?? '1') || 1;

let currentSettings = loadSettings(window.localStorage);
let musicEnabled = currentSettings.musicEnabled;
let highScoreSeen = currentSettings.highScore;
let gamePaused = false;
const initial: GameState = { ...createGame({ seed }), phase: 'title' };
const input = attachKeyboard(window);
const pause = attachPause(window, 'KeyP');
const audio = createAudioAdapter(createWebAudioSynth());
audio.setVolume(currentSettings.volume);

// AIDEV-NOTE: Music tracks are globbed from /music/*.mp3 at build time. The
// player starts on the first user gesture (autoplay policy) and alternates
// tracks in cyclic order, picking the initial one at random.
const musicModules = import.meta.glob('/music/*.mp3', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;
const musicTracks = Object.values(musicModules);
const music = createMusicPlayer({ tracks: musicTracks, audio: new Audio() });
music.setVolume(currentSettings.volume);

function persistSettings(): void {
  saveSettings(window.localStorage, currentSettings);
}

function syncMusic(): void {
  music.setPaused(!musicEnabled || gamePaused);
}

let spriteSheet: SpriteSheet | undefined;
const spriteImage = new Image();
spriteImage.decoding = 'async';
spriteImage.onload = () => {
  spriteSheet = createRallySpriteSheet(spriteImage);
};
spriteImage.src = RALLYXY_SPRITE_URL;

let hudLogo: CanvasImageSource | undefined;
const logoImage = new Image();
logoImage.decoding = 'async';
logoImage.onload = () => {
  hudLogo = logoImage;
};
logoImage.src = RALLYXY_LOGO_URL;

// Browsers gate AudioContext on the first user gesture.
window.addEventListener('keydown', () => {
  void audio.resume();
  if (musicEnabled) void music.start();
}, { once: true });
window.addEventListener('pointerdown', () => {
  void audio.resume();
  if (musicEnabled) void music.start();
}, { once: true });

window.addEventListener('keydown', (e) => {
  if (e.repeat) return;
  if (e.code === 'BracketLeft') {
    const v = Math.max(0, currentSettings.volume - 0.1);
    audio.setVolume(v);
    music.setVolume(v);
    currentSettings = { ...currentSettings, volume: v };
    persistSettings();
  } else if (e.code === 'BracketRight') {
    const v = Math.min(1, currentSettings.volume + 0.1);
    audio.setVolume(v);
    music.setVolume(v);
    currentSettings = { ...currentSettings, volume: v };
    persistSettings();
  } else if (e.code === 'KeyM') {
    musicEnabled = !musicEnabled;
    currentSettings = { ...currentSettings, musicEnabled };
    persistSettings();
    if (musicEnabled) void music.start();
    syncMusic();
  } else if (e.code === 'KeyF') {
    toggleFullscreen();
  }
});

// Fullscreen toggle. Swallows rejections from unsupported (iOS Safari on
// non-video elements), denied (permissions policy in embedded contexts),
// or user-cancelled requests; the worst case is the keypress no-ops.
function toggleFullscreen(): void {
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => {});
  } else if (canvas.requestFullscreen) {
    void canvas.requestFullscreen().catch(() => {});
  }
}

const handle = startLoop({
  initial,
  step,
  pollInput: input.poll,
  isPaused: pause.isPaused,
  onAfterStep: (state) => {
    audio.consume(state.events);
    if (state.score > highScoreSeen) highScoreSeen = state.score;
    if (state.events.some((e) => e.kind === 'game_over') && highScoreSeen > currentSettings.highScore) {
      currentSettings = { ...currentSettings, highScore: highScoreSeen };
      persistSettings();
    }
  },
  render: (state) => {
    const paused = pause.isPaused();
    if (paused !== gamePaused) {
      gamePaused = paused;
      syncMusic();
    }
    const highScore = Math.max(highScoreSeen, state.score);
    render(ctx, state, { spriteSheet, hudLogo, highScore });
    if (paused) drawPauseBanner(ctx);
  },
});

declare global {
  interface Window {
    __game?: {
      getState: () => GameState;
      seed: number;
    };
  }
}

window.__game = {
  getState: handle.getState,
  seed,
};
